'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useSiteContent } from './SiteContent';

/**
 * <E id default/> — one editable copy field. Reads the published value for
 * visitors; becomes inline click-to-edit for an admin in ?edit=1 mode. If the id
 * is in the page's rich set it renders/edits as sanitized rich HTML (bold /
 * italic / link); otherwise plain text.
 */

// Substitute {token} placeholders with live values (read mode only; the editor
// shows the raw template with the tokens visible so an admin can move them).
const applyTokens = (s: string, tokens?: Record<string, string>) =>
  tokens ? s.replace(/\{(\w+)\}/g, (m, k) => (k in tokens ? tokens[k] : m)) : s;

const looksLikeHtml = (s: string) => /<[a-z][\s\S]*>/i.test(s);
const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const plainToHtml = (s: string) =>
  s
    .split(/\n\s*\n/)
    .map((p) => `<p>${escapeHtml(p.trim())}</p>`)
    .join('');

type Tag = keyof React.JSX.IntrinsicElements;

export function E({
  id,
  default: def = '',
  as = 'span',
  className,
  style,
  multiline,
  placeholder,
  tokens,
}: {
  id: string;
  default?: string;
  as?: Tag;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  placeholder?: string;
  tokens?: Record<string, string>;
}) {
  const { value, editing, rich, onEdit } = useSiteContent();
  const isRich = rich.has(id);
  const v = value(id, def);

  if (!editing) {
    if (isRich) {
      if (!v) return React.createElement(as, { className, style });
      if (looksLikeHtml(v))
        return React.createElement(as, {
          className,
          style,
          dangerouslySetInnerHTML: { __html: applyTokens(v, tokens) },
        });
      return React.createElement(
        as,
        { className, style },
        applyTokens(v, tokens)
          .split(/\n\s*\n/)
          .map((p, i) => <p data-component-id="src/components/site-content/Editable.tsx:66:25" data-component-path="src/components/site-content/Editable.tsx" data-component-line="66" data-component-end-line="66" data-component-file="Editable.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" key={i}>{p.trim()}</p>)
      );
    }
    return React.createElement(as, { className, style }, applyTokens(v, tokens) || null);
  }

  if (isRich)
    return (
      <RichEdit data-component-id="src/components/site-content/Editable.tsx:74:6" data-component-path="src/components/site-content/Editable.tsx" data-component-line="74" data-component-end-line="81" data-component-file="Editable.tsx" data-component-name="RichEdit" data-component-content="%7B%22elementName%22%3A%22RichEdit%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D"
        id={id}
        v={v}
        onEdit={onEdit}
        className={className}
        style={style}
        placeholder={placeholder}
      />
    );
  return (
    <PlainEdit data-component-id="src/components/site-content/Editable.tsx:84:4" data-component-path="src/components/site-content/Editable.tsx" data-component-line="84" data-component-end-line="93" data-component-file="Editable.tsx" data-component-name="PlainEdit" data-component-content="%7B%22elementName%22%3A%22PlainEdit%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D"
      id={id}
      v={v}
      onEdit={onEdit}
      as={as}
      className={className}
      style={style}
      multiline={multiline}
      placeholder={placeholder}
    />
  );
}

function PlainEdit({
  id,
  v,
  onEdit,
  as = 'span',
  className,
  style,
  multiline,
  placeholder,
}: {
  id: string;
  v: string;
  onEdit: (id: string, val: string) => void;
  as?: Tag;
  className?: string;
  style?: React.CSSProperties;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [editingNow, setEditingNow] = useState(false);
  if (editingNow) {
    const common = {
      autoFocus: true,
      defaultValue: v,
      style,
      className: `${className || ''} block w-full rounded bg-white/90 px-1 text-inherit outline outline-2 outline-teal-500`,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setEditingNow(false);
        if (e.target.value !== v) onEdit(id, e.target.value);
      },
    };
    return multiline ? (
      <textarea data-component-id="src/components/site-content/Editable.tsx:129:6" data-component-path="src/components/site-content/Editable.tsx" data-component-line="129" data-component-end-line="133" data-component-file="Editable.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
        {...common}
        rows={5}
        onKeyDown={(e) => e.key === 'Escape' && setEditingNow(false)}
      />
    ) : (
      <input data-component-id="src/components/site-content/Editable.tsx:135:6" data-component-path="src/components/site-content/Editable.tsx" data-component-line="135" data-component-end-line="141" data-component-file="Editable.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
        {...common}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setEditingNow(false);
        }}
      />
    );
  }
  return React.createElement(
    as,
    {
      className: `${className || ''} cursor-text rounded outline-dashed outline-2 outline-transparent transition hover:bg-teal-50/50 hover:outline-teal-300`,
      style,
      onClick: () => setEditingNow(true),
      title: 'Click to edit',
    },
    v || <span data-component-id="src/components/site-content/Editable.tsx:152:9" data-component-path="src/components/site-content/Editable.tsx" data-component-line="152" data-component-end-line="152" data-component-file="Editable.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22italic%20opacity-40%22%7D" className="italic opacity-40">{placeholder || 'Click to edit'}</span>
  );
}

function RichEdit({
  id,
  v,
  onEdit,
  className,
  style,
  placeholder,
}: {
  id: string;
  v: string;
  onEdit: (id: string, val: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [bar, setBar] = useState(false);
  const seed = v ? (looksLikeHtml(v) ? v : plainToHtml(v)) : '';

  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.innerHTML !== seed) el.innerHTML = seed;
  }, [seed]);

  const exec = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
  };
  const commit = () => {
    setBar(false);
    const html = ref.current?.innerHTML ?? '';
    if (html !== seed) onEdit(id, html);
  };
  const B = ({ label, on }: { label: string; on: () => void }) => (
    <button data-component-id="src/components/site-content/Editable.tsx:190:4" data-component-path="src/components/site-content/Editable.tsx" data-component-line="190" data-component-end-line="199" data-component-file="Editable.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22rounded%20px-2%20py-0.5%20text-xs%20font-semibold%20text-white%2F90%20hover%3Abg-white%2F15%22%7D"
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        on();
      }}
      className="rounded px-2 py-0.5 text-xs font-semibold text-white/90 hover:bg-white/15"
    >
      {label}
    </button>
  );

  return (
    <div data-component-id="src/components/site-content/Editable.tsx:203:4" data-component-path="src/components/site-content/Editable.tsx" data-component-line="203" data-component-end-line="234" data-component-file="Editable.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
      {bar && (
        <div data-component-id="src/components/site-content/Editable.tsx:205:8" data-component-path="src/components/site-content/Editable.tsx" data-component-line="205" data-component-end-line="216" data-component-file="Editable.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-9%20left-0%20z-30%20flex%20items-center%20gap-0.5%20rounded-lg%20bg-slate-900%2F95%20px-1%20py-1%20shadow-xl%20ring-1%20ring-white%2F10%22%7D" className="absolute -top-9 left-0 z-30 flex items-center gap-0.5 rounded-lg bg-slate-900/95 px-1 py-1 shadow-xl ring-1 ring-white/10">
          <B data-component-id="src/components/site-content/Editable.tsx:206:10" data-component-path="src/components/site-content/Editable.tsx" data-component-line="206" data-component-end-line="206" data-component-file="Editable.tsx" data-component-name="B" data-component-content="%7B%22elementName%22%3A%22B%22%7D" label="B" on={() => exec('bold')} />
          <B data-component-id="src/components/site-content/Editable.tsx:207:10" data-component-path="src/components/site-content/Editable.tsx" data-component-line="207" data-component-end-line="207" data-component-file="Editable.tsx" data-component-name="B" data-component-content="%7B%22elementName%22%3A%22B%22%7D" label="I" on={() => exec('italic')} />
          <B data-component-id="src/components/site-content/Editable.tsx:208:10" data-component-path="src/components/site-content/Editable.tsx" data-component-line="208" data-component-end-line="214" data-component-file="Editable.tsx" data-component-name="B" data-component-content="%7B%22elementName%22%3A%22B%22%7D"
            label="Link"
            on={() => {
              const url = window.prompt('Link URL (https://…):', 'https://');
              if (url) exec('createLink', url);
            }}
          />
          <B data-component-id="src/components/site-content/Editable.tsx:215:10" data-component-path="src/components/site-content/Editable.tsx" data-component-line="215" data-component-end-line="215" data-component-file="Editable.tsx" data-component-name="B" data-component-content="%7B%22elementName%22%3A%22B%22%7D" label="Clear" on={() => exec('removeFormat')} />
        </div>
      )}
      <div data-component-id="src/components/site-content/Editable.tsx:218:6" data-component-path="src/components/site-content/Editable.tsx" data-component-line="218" data-component-end-line="228" data-component-file="Editable.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onFocus={() => setBar(true)}
        onBlur={commit}
        data-sc-rich={id}
        className={`${className || ''} cursor-text rounded outline-dashed outline-2 outline-transparent transition focus:bg-white/60 focus:outline-teal-500 hover:outline-teal-300`}
        style={style}
        title="Click to edit · select text for bold, italic, link"
      />
      {!v && !bar && (
        <span data-component-id="src/components/site-content/Editable.tsx:230:8" data-component-path="src/components/site-content/Editable.tsx" data-component-line="230" data-component-end-line="232" data-component-file="Editable.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22pointer-events-none%20absolute%20left-1%20top-0%20text-sm%20italic%20opacity-40%22%7D" className="pointer-events-none absolute left-1 top-0 text-sm italic opacity-40">
          {placeholder || 'Click to edit'}
        </span>
      )}
    </div>
  );
}
